import React, { useState, useMemo, useCallback } from 'react';
import { FUENTE, useTema } from './tema';
import { publicarEnBiblioteca, bibliotecaConfigurada } from '../../services/actaPublicada';
import { useConfirmacion } from './Confirmacion';

/**
 * PublicarActa — llevar el acta terminada a la biblioteca del equipo.
 *
 * Es deliberadamente un botón aparte del de «Ver como Word», y no un destino
 * más de aquel. Ver es privado: deja una copia en tu OneDrive para poder
 * empotrarla. Publicar es público: a partir de ese momento el acta la ve todo
 * el que tenga acceso al sitio de SharePoint.
 *
 * Si subir a la biblioteca ocurriera al abrir la vista previa, mirar un
 * borrador lo publicaría al equipo entero sin que nadie lo hubiera pedido. Un
 * acta a medio revisar circulando como definitiva es peor que no tener acta.
 *
 * Por eso además se confirma: publicar no se deshace desde aquí —el archivo
 * queda en la biblioteca, con su historial de versiones— y quien pulsa tiene
 * que saber a dónde va antes de pulsar.
 */

const estilos = (C) => ({
  boton: {
    display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 13px',
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 8,
    color: C.texto, fontSize: 13, fontFamily: FUENTE, cursor: 'pointer', whiteSpace: 'nowrap'
  },
  recado: (malo) => ({
    flexBasis: '100%', order: 2, display: 'flex', alignItems: 'center', gap: 10,
    fontSize: 12.5, lineHeight: 1.5, color: malo ? C.atencion : C.suave
  }),
  enlace: { color: C.acentoClaro }
});

/**
 * @param {string|null} props.base64 - el .docx generado
 * @param {string} props.titulo
 * @param {Date|string|null} props.fecha
 * @param {Function} props.tokenGraph
 */
export function usePublicarActa({ base64 = null, titulo = '', fecha = null, tokenGraph }) {

  const [trabajando, setTrabajando] = useState(false);
  const [dialogo, confirmar] = useConfirmacion();
  const [recado, setRecado] = useState(null);

  const config = useMemo(() => bibliotecaConfigurada(), []);

  const publicar = useCallback(async () => {
    if (!base64) return;

    // Publicar no se deshace desde la aplicación, así que se dice a dónde va y
    // se pregunta. No es una descarga: es dejar un documento en un sitio
    // compartido con el nombre de quien lo publica.
    if (!await confirmar({
      titulo: 'Publicar el acta al equipo',
      mensaje: `Se guardará en la biblioteca del equipo:\n\n${config.sitio}\ncarpeta «${config.carpeta}»\n\n` +
        'A partir de ahí la verá todo el que tenga acceso a ese sitio, y desde aquí no se ' +
        'puede retirar.',
      aceptar: 'Publicarla'
    })) return;

    setTrabajando(true);
    setRecado(null);
    try {
      const item = await publicarEnBiblioteca(await tokenGraph(), { base64, titulo, fecha });
      setRecado({ texto: `Publicada como «${item.nombre}».`, url: item.webUrl, malo: false });
    } catch (e) {
      setRecado({ texto: e.message || 'No se pudo publicar el acta.', malo: true });
    } finally {
      setTrabajando(false);
    }
  }, [base64, titulo, fecha, tokenGraph, config, confirmar]);

  // Sin biblioteca configurada, publicar no se ofrece: proponer algo que no
  // puede funcionar y explicarlo al pulsarlo es peor que no proponerlo. Quien
  // despliega lo configura con REACT_APP_ACTAS_SITIO.
  return {
    disponible: Boolean(config && base64),
    destino: config,
    publicar,
    trabajando,
    recado,
    dialogo
  };
}

/** Lo que salió de publicar, con el enlace a lo publicado. */
export const RecadoDePublicacion = ({ recado }) => {
  const { C } = useTema();
  const S = useMemo(() => estilos(C), [C]);
  if (!recado) return null;

  return (
    <div style={S.recado(recado.malo)} role="status">
      <span>{recado.texto}</span>
      {recado.url && (
        <a href={recado.url} target="_blank" rel="noreferrer" style={S.enlace}>Abrirla</a>
      )}
    </div>
  );
};

export default usePublicarActa;
